import itPersonnelCosts from './locales/it/personnel-costs.json';
import enPersonnelCosts from './locales/en/personnel-costs.json';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { registerUiTranslations } from '@repo/ui/i18n';

// Import sincrono di tutti i moduli
import itCommon from './locales/it/common.json';
import itLayout from './locales/it/layout.json';
import itAuth from './locales/it/auth.json';
import itDashboard from './locales/it/dashboard.json';
import itDocuments from './locales/it/documents.json';
import itAccounts from './locales/it/accounts.json';
import itForecast from './locales/it/forecast.json';
import itReconciliation from './locales/it/reconciliation.json';
import itSettings from './locales/it/settings.json';
import itCategories from './locales/it/categories.json';
import itFinance from './locales/it/finance.json';
import itSync from './locales/it/sync.json';
import itRegistry from './locales/it/registry.json';
import itPayrollImport from './locales/it/payroll-import.json';
import itHelp from './locales/it/help.json';
import itIntegrations from './locales/it/integrations.json';
import itWarehouse from './locales/it/warehouse.json';
import itPriceList from './locales/it/price-list.json';
import itSupport from './locales/it/support.json';
import itStudio from './locales/it/studio.json';

import enCommon from './locales/en/common.json';
import enLayout from './locales/en/layout.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enDocuments from './locales/en/documents.json';
import enAccounts from './locales/en/accounts.json';
import enForecast from './locales/en/forecast.json';
import enReconciliation from './locales/en/reconciliation.json';
import enSettings from './locales/en/settings.json';
import enCategories from './locales/en/categories.json';
import enFinance from './locales/en/finance.json';
import enSync from './locales/en/sync.json';
import enRegistry from './locales/en/registry.json';
import enPayrollImport from './locales/en/payroll-import.json';
import enHelp from './locales/en/help.json';
import enIntegrations from './locales/en/integrations.json';
import enWarehouse from './locales/en/warehouse.json';
import enPriceList from './locales/en/price-list.json';
import enSupport from './locales/en/support.json';
import enStudio from './locales/en/studio.json';
import itCommesse from './locales/it/commesse.json';
import enCommesse from './locales/en/commesse.json';
import itServiceContracts from './locales/it/service-contracts.json';
import enServiceContracts from './locales/en/service-contracts.json';
import itWorkReports from './locales/it/work-reports.json';
import enWorkReports from './locales/en/work-reports.json';
import itMachines from './locales/it/machines.json';
import enMachines from './locales/en/machines.json';
import itMachineChecks from './locales/it/machine-checks.json';
import enMachineChecks from './locales/en/machine-checks.json';
import itHotel from './locales/it/hotel.json';
import enHotel from './locales/en/hotel.json';
import itF24 from './locales/it/f24.json';
import enF24 from './locales/en/f24.json';

// Mapping dei namespace ai moduli (per riferimento)
export const TRANSLATION_MODULES = {
  common: [
    'commons',
    'errors',
    'form',
    'pagination',
    'calendar',
    'toast',
    'fileUpload',
    'enums',
  ],
  auth: ['auth', 'sign_up', 'resetPassword', 'confirmEmail', 'invitation'],
  layout: [
    'sidebar',
    'navbar',
    'command',
    'keyboard_shortcuts',
    'notifications',
  ],
  dashboard: ['dashboard', 'ai_assistants', 'chat'],
  documents: [
    'document',
    'document_line',
    'documentPaymentSchedule',
    'documentWithSchedules',
    'document_attachments',
    'export',
  ],
  accounts: [
    'account',
    'cash_movement',
    'cashMovementAllocation',
    'bankImport',
  ],
  forecast: [
    'forecastSeries',
    'forecastOccurrence',
    'forecastPaymentSchedule',
    'cashFlowForecast',
    'cashFlow',
  ],
  reconciliation: ['reconciliation', 'entityDetails'],
  settings: [
    'preferences',
    'tenantSettings',
    'user',
    'company',
    'contacts',
    'billing',
  ],
  categories: ['category', 'categorySuggestions', 'autoCategorization'],
  finance: ['debts', 'dscr', 'payment', 'payments', 'tax_box'],
  sync: ['sync', '404_not_found', 'tenant', 'onboarding'],
  registry: ['registry'],
  payrollImport: ['payrollImport'],
  help: ['help'],
  integrations: ['integrations'],
  warehouse: ['lowStock', 'warehouse'],
  priceList: ['price_list'],
  support: ['support'],
  studio: ['advisor'],
  commesse: ['commesse'],
  serviceContracts: ['service_contracts'],
  workReports: ['work_reports'],
  hotel: ['hotel'],
  f24: ['f24'],
} as const;

export type TranslationModule = keyof typeof TRANSLATION_MODULES;

// Combina tutti i moduli in un unico oggetto translation per lingua
const itTranslation = {
  ...itPersonnelCosts,
  ...itCommon,
  ...itLayout,
  ...itAuth,
  ...itDashboard,
  ...itDocuments,
  ...itAccounts,
  ...itForecast,
  ...itReconciliation,
  ...itSettings,
  ...itCategories,
  ...itFinance,
  ...itSync,
  ...itRegistry,
  ...itPayrollImport,
  ...itHelp,
  ...itIntegrations,
  ...itWarehouse,
  ...itPriceList,
  ...itSupport,
  ...itStudio,
  ...itCommesse,
  ...itServiceContracts,
  ...itWorkReports,
  ...itMachines,
  ...itMachineChecks,
  ...itHotel,
  ...itF24,
};

const enTranslation = {
  ...enPersonnelCosts,
  ...enCommon,
  ...enLayout,
  ...enAuth,
  ...enDashboard,
  ...enDocuments,
  ...enAccounts,
  ...enForecast,
  ...enReconciliation,
  ...enSettings,
  ...enCategories,
  ...enFinance,
  ...enSync,
  ...enRegistry,
  ...enPayrollImport,
  ...enHelp,
  ...enIntegrations,
  ...enWarehouse,
  ...enPriceList,
  ...enSupport,
  ...enStudio,
  ...enCommesse,
  ...enServiceContracts,
  ...enWorkReports,
  ...enMachines,
  ...enMachineChecks,
  ...enHotel,
  ...enF24,
};

const resources = {
  it: {
    translation: itTranslation,
  },
  en: {
    translation: enTranslation,
  },
};

i18n.use(initReactI18next).init({
  // For the moment we use Italian as only language instead of userLanguage()
  lng: 'it',
  resources,
  fallbackLng: 'it',
  returnNull: false,
  interpolation: {
    escapeValue: false, // React already handles XSS protection
  },
});

// Testi dei componenti di @repo/ui, nel loro namespace `ui`.
registerUiTranslations(i18n);

export default i18n;
